import { z } from "zod";
import { zEmail, zId } from "./common.js";

/** The one password rule for every place a user chooses a password (signup, reset, change). */
export const PASSWORD_MIN_LENGTH = 12;
export const zPassword = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(256, "Password must be 256 characters or fewer");

export const zSignupRequest = z.object({
  email: zEmail,
  password: zPassword,
  name: z.string().trim().min(1, "Enter your name").max(200, "Name must be 200 characters or fewer"),
  organizationName: z
    .string()
    .trim()
    .min(2, "Organization name must be at least 2 characters")
    .max(200, "Organization name must be 200 characters or fewer"),
});
export type SignupRequest = z.infer<typeof zSignupRequest>;

export const zLoginRequest = z.object({
  email: zEmail,
  password: z.string().min(1).max(256),
  totpCode: z.string().regex(/^\d{6}$/).optional(),
});
export type LoginRequest = z.infer<typeof zLoginRequest>;

export const zLoginResponse = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ok") }),
  z.object({ status: z.literal("totp_required") }),
]);
export type LoginResponse = z.infer<typeof zLoginResponse>;

export const zSessionUser = z.object({
  id: zId,
  email: zEmail,
  name: z.string(),
  organizationId: zId,
  organizationName: z.string(),
  organizationLogo: z.string().nullable().default(null),
  roleKey: z.string(),
  permissions: z.array(z.string()),
  preferences: z.record(z.unknown()).default({}),
  /** CT-RM-22: true inside a developer sandbox org (the UI shows a Sandbox banner) */
  isSandbox: z.boolean().default(false),
  /** the production org a sandbox belongs to */
  sandboxParentName: z.string().nullable().default(null),
});
export type SessionUser = z.infer<typeof zSessionUser>;

/** GET /auth/session — always 200; user is null when signed out. */
export const zSessionStatusResponse = z.object({ user: zSessionUser.nullable() });
export type SessionStatusResponse = z.infer<typeof zSessionStatusResponse>;

export const zTotpSetupResponse = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
});

export const zTotpVerifyRequest = z.object({
  code: z.string().regex(/^\d{6}$/),
});

// ---- password reset / change ----

/** Shown for every forgot-password submission so the response never reveals whether an account exists. */
export const PASSWORD_RESET_NEUTRAL_MESSAGE = "If an account exists for that email, we've sent a reset link.";
export const PASSWORD_RESET_TTL_MINUTES = 60;

export const zForgotPasswordRequest = z.object({
  email: zEmail,
});
export type ForgotPasswordRequest = z.infer<typeof zForgotPasswordRequest>;

export const zForgotPasswordResponse = z.object({
  status: z.literal("ok"),
  message: z.string(),
});
export type ForgotPasswordResponse = z.infer<typeof zForgotPasswordResponse>;

export const zResetPasswordRequest = z.object({
  token: z.string().trim().min(16, "This reset link is incomplete").max(256),
  password: zPassword,
});
export type ResetPasswordRequest = z.infer<typeof zResetPasswordRequest>;

export const zValidateResetTokenRequest = z.object({
  token: z.string().trim().min(1).max(256),
});
export type ValidateResetTokenRequest = z.infer<typeof zValidateResetTokenRequest>;

export const zValidateResetTokenResponse = z.object({ valid: z.boolean() });
export type ValidateResetTokenResponse = z.infer<typeof zValidateResetTokenResponse>;

/** Donor-portal magic links expire this long after they're issued. */
export const PORTAL_LINK_TTL_MINUTES = 30;

export const zChangePasswordRequest = z.object({
  currentPassword: z.string().min(1, "Enter your current password").max(256),
  newPassword: zPassword,
});
export type ChangePasswordRequest = z.infer<typeof zChangePasswordRequest>;

export const zSessionInfo = z.object({
  id: zId,
  createdAt: z.string(),
  lastSeenAt: z.string(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  current: z.boolean(),
});
export type SessionInfo = z.infer<typeof zSessionInfo>;
